import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { warrantiesInfo, warrantyClaimsInfo } from '../../../../../core/projects.selectors';
import {
  FetchWarrantiesRequest,
  FetchWarrantyClaimsRequest,
} from '../../../../../core/projects.actions';
import { ProjectsService } from '../../../../../core/projects.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { AuthService } from '../../../../../../../shared/services';
import { WarrantyClaimDetailComponent } from './warranty-claim-detail.component';
import { WarrantyClaimFormComponent } from './warranty-claim-form.component';
import { WarrantyPackageFormComponent } from './warranty-package-form.component';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import { saveAs } from 'file-saver';
import { Ability, subject } from '@casl/ability';
import { TabbedFilterGroup } from '../tabbed-filter/tabbed-filter.component';

@Component({
  selector: 'app-warranty-claims-list',
  templateUrl: './warranty-claims-list.component.html',
  styleUrls: ['./warranty-claims-list.component.scss'],
  standalone: false,
})
export class WarrantyClaimsListComponent implements OnInit, OnDestroy {
  projectId: string;
  canCreate = false;
  warranty: any = null;
  warranties: any[] = [];
  claims: any[] = [];
  totalRecords = 0;
  selectedRow: any = null;
  account: any;
  role: string;

  // filters/search/pagination/sort state
  form: FormGroup;
  pagination = { start: 0, pageSize: 10 };
  selectedStatuses: string[] = [];
  selectedCreators: string[] = [];
  /** Populated from the claim-creators endpoint — only people who actually
   *  filed a claim on this warranty, so the filter never offers an empty result. */
  createdByList: { value: string; label: string; isChecked: boolean }[] = [];
  /** Tabs of the single "Filters (N)" button. Rebuilt whenever the creator
   *  list arrives so the Created-by tab only shows once it has people in it. */
  filterGroups: TabbedFilterGroup[] = [];
  sortByColumns = ['Claim #', 'Title', 'Severity', 'Status', 'Created'];
  selectedSortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'desc';
  private readonly sortFieldMap: Record<string, string> = {
    'Claim #': 'claim_number',
    'Title': 'title',
    'Severity': 'severity',
    'Status': 'status',
    'Created': 'created_at',
  };
  statusList = [
    { value: 'draft', label: 'Draft', isChecked: false },
    { value: 'submitted', label: 'Submitted', isChecked: false },
    { value: 'sent_back', label: 'Sent back to inspector', isChecked: false },
    { value: 'sent_to_contractor', label: 'Sent to contractor', isChecked: false },
    { value: 'responded', label: 'Contractor responded', isChecked: false },
    { value: 'verified', label: 'Verified', isChecked: false },
    { value: 'closed', label: 'Closed', isChecked: false },
  ];

  severityMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    low: { label: 'Low', bgColor: 'bg-low', textColor: 'txt-low' },
    medium: { label: 'Medium', bgColor: 'bg-medium', textColor: 'txt-medium' },
    high: { label: 'High', bgColor: 'bg-high', textColor: 'txt-high' },
    critical: { label: 'Critical', bgColor: 'bg-critical', textColor: 'txt-critical' },
  };

  statusMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    draft: { label: 'Draft', bgColor: 'bg-draft', textColor: 'txt-draft' },
    sent_back: { label: 'Sent back to inspector', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
    submitted: { label: 'Submitted', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    pm_review: { label: 'PM Review', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    approved: { label: 'Approved', bgColor: 'bg-approved', textColor: 'txt-approved' },
    rejected: { label: 'Rejected', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
    sent_to_contractor: { label: 'Sent to contractor', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    contractor_notified: { label: 'Sent to contractor', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    responded: { label: 'Contractor responded', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    verified: { label: 'Verified', bgColor: 'bg-approved', textColor: 'txt-approved' },
    in_progress: { label: 'In Progress', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    disputed: { label: 'Disputed', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
    remediation_complete: { label: 'Remediation Complete', bgColor: 'bg-approved', textColor: 'txt-approved' },
    closed: { label: 'Closed', bgColor: 'bg-approved', textColor: 'txt-approved' },
  };

  displayedColumns = ['claim_number', 'title', 'severity', 'status', 'created_at', 'created_by', 'actions'];

  /**
   * Deep link from global search: `?claimId=` auto-opens the claim's right
   * panel on load, one-shot, the same way a row click would.
   */
  private pendingDeepLinkClaimId: string = null;
  /** Set when the deep-linked claim wasn't in the fetched page: after the
   * list refetches (via the search box seeded with the claim's title), the
   * matching row gets the selected styling and is scrolled into view. */
  private pendingHighlightClaimId: string = null;
  /** False until the store's synchronous replay of the previous claims state
   * has passed; deep-link consumption only trusts fresh emissions. */
  private listReady = false;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private authService: AuthService,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private appHttpService: AppHttpService,
    private ability: Ability,
  ) {
    this.form = new FormGroup({ searchText: new FormControl('') });
  }

  ngOnInit() {
    this.pendingDeepLinkClaimId = this.route.snapshot.queryParamMap.get('claimId');
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.buildFilterGroups();
    this.account = this.authService.getCurrentUser();
    this.role = this.account?.roles?.find(r => r.project_id === this.projectId)?.role || '';
    this.canCreate = this.ability.can('create', subject('WarrantyClaim', { project_id: this.projectId }));

    // Fetch warranties first to get warranty ID
    this.store.dispatch(FetchWarrantiesRequest({ payload: { projectId: this.projectId } }));

    this.store.select(warrantiesInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(info => {
        if (info?.list?.length) {
          this.warranties = info.list;
          // Seed the selection only when there isn't a valid one. The store
          // replays, and re-seeding to list[0] on every emission undid a deep
          // link that had switched to the claim's OWN warranty — refetching
          // the first warranty's claims over it and losing the opened row.
          const selectedId = this.warranty?.uuid || this.warranty?.id;
          const stillSelectable = selectedId
            && info.list.some(item => (item?.uuid || item?.id) === selectedId);
          if (!stillSelectable) {
            this.warranty = info.list[0];
            this.fetchClaims();
            this.fetchClaimCreators();
          }
        }
      });

    // Status and Created-by filters both live in the URL query params
    // (status=draft&status=closed, created_by=<accountUuid>) — same as RFI
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        const asList = (v: any) => (v ? (Array.isArray(v) ? v : [v]) : []);
        const nextStatuses = asList(qp.status);
        const nextCreators = asList(qp.created_by);

        const differs = (next: string[], current: string[]) =>
          next.length !== current.length || next.some(v => !current.includes(v));

        const statusChanged = differs(nextStatuses, this.selectedStatuses);
        const creatorChanged = differs(nextCreators, this.selectedCreators);

        if (statusChanged || creatorChanged) {
          this.selectedStatuses = nextStatuses;
          this.selectedCreators = nextCreators;
          this.pagination.start = 0;
          this.fetchClaims();
        }
      });

    this.store.select(warrantyClaimsInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(info => {
        if (info) {
          this.claims = (info.list || []).map(c => ({
            ...c,
            ...(c.attributes || {}),
          }));
          this.totalRecords = info.totalRecords;

          // Each emission rebuilds the row objects, so re-point the reference
          // the highlighted-row styling compares against.
          if (this.selectedRow) {
            const selectedId = this.selectedRow.uuid || this.selectedRow.id;
            const current = this.claims.find(row => (row?.uuid || row?.id) === selectedId);
            if (current) {
              this.selectedRow = current;
            }
          }

          // Skip the synchronous replay of the previous store state (possibly
          // another project's claims): only fresh fetch responses may consume
          // the deep link.
          if (this.listReady) {
            this.tryOpenDeepLinkedClaim();
            this.tryHighlightPendingClaim();
          }
        }
      });
    this.listReady = true;

    // Debounced search
    this.form.get('searchText')?.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(() => {
        this.pagination.start = 0;
        this.fetchClaims();
      });

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => (this.selectedRow = null));
  }

  /** Load the distinct claim authors that populate the "Created by" filter. */
  fetchClaimCreators(): void {
    if (!this.warranty?.id || !this.projectId) return;
    this.appHttpService
      .getService(`/v1/warranty/project/${this.projectId}/warranties/${this.warranty.id}/claim-creators`)
      .subscribe({
        next: (resp: any) => {
          this.createdByList = (resp?.data || []).map((c: any) => ({
            value: c.id,
            label: c.name,
            isChecked: this.selectedCreators.includes(c.id),
          }));
          this.buildFilterGroups();
        },
        error: () => {
          this.createdByList = [];
          this.buildFilterGroups();
        },
      });
  }

  private buildFilterGroups(): void {
    this.filterGroups = [
      { label: 'Status', queryParam: 'status', options: this.statusList },
      ...(this.createdByList.length
        ? [{ label: 'Created by', queryParam: 'created_by', options: this.createdByList, searchable: true }]
        : []),
    ];
  }

  fetchClaims(): void {
    if (!this.warranty?.id) return;
    const payload: any = {
      projectId: this.projectId,
      warrantyId: this.warranty.id,
      start: this.pagination.start,
      total: this.pagination.pageSize,
    };
    const search = this.form.get('searchText')?.value?.trim();
    if (search) payload.search = search;
    if (this.selectedStatuses.length) payload.status = this.selectedStatuses;
    if (this.selectedCreators.length) payload.created_by = this.selectedCreators;
    if (this.selectedSortColumn) {
      const field = this.sortFieldMap[this.selectedSortColumn];
      if (field) {
        payload.sort_by = field;
        payload.sort_order = this.sortDirection;
      }
    }
    this.store.dispatch(FetchWarrantyClaimsRequest({ payload }));
  }

  onPageChanged(page: number): void {
    this.pagination.start = (page - 1) * this.pagination.pageSize;
    this.fetchClaims();
  }

  clearSearch(): void {
    this.form.get('searchText')?.setValue('');
  }

  onSortColumnChange(event: any): void {
    this.selectedSortColumn = event?.value ?? null;
    this.sortDirection = 'desc';
    this.pagination.start = 0;
    this.fetchClaims();
  }

  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.fetchClaims();
  }

  removeSort(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedSortColumn = null;
    this.fetchClaims();
  }

  @ViewChild('sortSelect') sortSelect?: MatSelect;

  openSortDropdown(event?: Event): void {
    // Material renders the select panel and its backdrop as an inline popover
    // *inside* this wrapper, so the click that closes the panel bubbles back
    // here and would reopen it. Only react to clicks on the button itself.
    const target = event?.target as HTMLElement | null;
    if (target?.closest('.cdk-overlay-popover, .cdk-overlay-backdrop')) return;
    setTimeout(() => this.sortSelect?.open());
  }

  /** Claims may only be created/edited while the parent warranty is active. */
  isWarrantyActive(): boolean {
    const status = this.warranty?.attributes?.status ?? this.warranty?.status;
    return status === 'active';
  }

  /**
   * Auto-opens the right panel for a deep-linked claim (?claimId=) once the
   * claims list has loaded, reusing the row-click data path. If the claim
   * isn't on the fetched page (server-side pagination, or it belongs to a
   * warranty other than the first), it is fetched by id instead; an
   * unknown/404 id no-ops silently. The refetch also guarantees full
   * attachment URLs (search-indexed docs may prune them).
   */
  private tryOpenDeepLinkedClaim(): void {
    if (!this.pendingDeepLinkClaimId) {
      return;
    }
    const id = this.pendingDeepLinkClaimId;
    this.pendingDeepLinkClaimId = null;

    const match = this.claims.find(row => `${row?.uuid || row?.id}` === `${id}`);
    if (match) {
      this.rowClicked(match);
      this.scrollHighlightedRowIntoView();
      return;
    }

    this.projectsService.fetchWarrantyClaimDetail({ claimId: id })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: resp => {
          const record = resp?.data
            ? { ...resp.data, ...(resp.data.attributes || {}) }
            : null;
          if (record?.id) {
            this.rowClicked(record);
            this.surfaceFetchedClaim(id, record);
          }
        },
        error: () => {
          // Deep-linked claim no longer exists (or is not accessible) — stay on the list.
        },
      });
  }

  /**
   * The deep-linked claim wasn't in the fetched page (server-side pagination,
   * or it belongs to a warranty other than the currently selected one):
   * switch to its warranty when known and seed the list's own search with its
   * title — the refetch response then highlights and scrolls to the row.
   */
  private surfaceFetchedClaim(id: string, record): void {
    const warrantyRef = record?.warranty_id ?? record?.warranty?.id;
    const targetWarranty = warrantyRef
      ? this.warranties.find(w => `${w?.uuid || w?.id}` === `${warrantyRef}`)
      : null;
    if (targetWarranty && targetWarranty !== this.warranty) {
      this.warranty = targetWarranty;
    }

    if (record?.title) {
      this.pendingHighlightClaimId = id;
      this.form.get('searchText')?.setValue(record.title);
    } else if (targetWarranty) {
      this.pendingHighlightClaimId = id;
      this.pagination.start = 0;
      this.fetchClaims();
    }
  }

  /** Applies the selected-row styling + scroll once the refetched list
   * contains the deep-linked claim that wasn't in the original page. */
  private tryHighlightPendingClaim(): void {
    if (!this.pendingHighlightClaimId) {
      return;
    }
    const row = this.claims.find(r => `${r?.uuid || r?.id}` === `${this.pendingHighlightClaimId}`);
    if (row) {
      this.pendingHighlightClaimId = null;
      this.selectedRow = row;
      this.scrollHighlightedRowIntoView();
    }
  }

  private scrollHighlightedRowIntoView(): void {
    setTimeout(() => {
      document.querySelector('app-warranty-claims-list .highlighted-row')
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }

  rowClicked(row: any): void {
    this.selectedRow = row;
    if (['draft', 'sent_back'].includes(row.status)) {
      // Editable claims may only be opened for editing while the warranty is active.
      if (!this.isWarrantyActive()) {
        this.commonService.notification(
          'Warranty is not active — claims cannot be edited.',
          'error',
        );
        // Fall back to the read-only detail view instead of the editable form.
        this.projectsService.openRightPanel.next({
          component: WarrantyClaimDetailComponent,
          data: { ...row },
        });
        return;
      }
      // Draft claims open in editable form (like RFI)
      this.projectsService.openRightPanel.next({
        component: WarrantyClaimFormComponent,
        data: {
          warranties: this.warranties,
          projectId: this.projectId,
          editClaim: { ...row },
        },
      });
    } else {
      this.projectsService.openRightPanel.next({
        component: WarrantyClaimDetailComponent,
        data: { ...row },
      });
    }
  }

  createClaim(): void {
    if (!this.isWarrantyActive()) {
      this.commonService.notification(
        'Warranty is not active — claims cannot be created.',
        'error',
      );
      return;
    }
    this.selectedRow = null;
    this.projectsService.openRightPanel.next({
      component: WarrantyClaimFormComponent,
      data: { warranties: this.warranties, projectId: this.projectId },
    });
  }

  isPM(): boolean {
    return this.role === 'project-manager' || this.role === 'org-admin';
  }

  // --- Claim downloads ---

  downloadClaimPdf(row: any): void {
    const claimId = row.uuid || row.id;
    this.appHttpService.getService(
      `/v1/warranty/claims/${claimId}/pdf`,
      {},
      { responseType: 'text' },
    ).subscribe({
      next: (html: string) => {
        const w = window.open('', '_blank');
        if (w) { w.document.write(html); w.document.close(); }
      },
    });
  }

  downloadClaimCsv(row: any): void {
    const claimId = row.uuid || row.id;
    this.appHttpService.getService(
      `/v1/warranty/claims/${claimId}/csv`,
      {},
      { responseType: 'text' },
    ).subscribe({
      next: (csv: string) => {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `${row.claim_number}.csv`);
      },
    });
  }

  downloadClaimKmz(row: any): void {
    const claimId = row.uuid || row.id;
    this.appHttpService.getService(
      `/v1/warranty/claims/${claimId}/kmz`,
      {},
      { responseType: 'blob' },
    ).subscribe({
      next: (resp: Blob) => saveAs(resp, `${row.claim_number}.kmz`),
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
